import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import userService from '../../common/api/userService';

import { RootState } from '../../app/store';
import {
  CLAIM_TYPES,
  LOADING_STATUS,
  PERMISSIONS,
  ROLES,
} from '../../common/constants';

export interface SiteClaim {
  id: string;
  name: string;
}

type AuthClaim = {
  type: string;
  value: string;
};

const ACTIVE_SITE_SESSION_KEY = 'activeSite';
const SITE_FALLBACK_PRIORITY = ['lomarengas', 'yllas', 'suomu'] as const;

const parseSiteClaimValue = (value: string | null): SiteClaim | null => {
  if (!value) {
    return null;
  }

  const [id, name] = value.split(';');
  if (!id || !name) {
    return null;
  }

  return { id, name };
};

const persistActiveSite = (activeSite: SiteClaim) => {
  const value = `${activeSite.id};${activeSite.name}`;
  sessionStorage.setItem(ACTIVE_SITE_SESSION_KEY, value);
  localStorage.setItem(ACTIVE_SITE_SESSION_KEY, value);
};

const getStoredActiveSite = (): SiteClaim | null =>
  parseSiteClaimValue(
    sessionStorage.getItem(ACTIVE_SITE_SESSION_KEY) ??
      localStorage.getItem(ACTIVE_SITE_SESSION_KEY),
  );

const getSiteClaimsFromAuthClaims = (authClaims: AuthClaim[] | null) => {
  const siteClaims = authClaims?.filter(
    claim => claim.type === CLAIM_TYPES.SitesType,
  );

  return siteClaims
    ?.map(siteClaim => parseSiteClaimValue(siteClaim.value))
    .filter(Boolean) as SiteClaim[];
};

const normalizeSiteName = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const getDefaultActiveSite = (siteClaims: SiteClaim[]): SiteClaim | null => {
  if (siteClaims.length === 0) {
    return null;
  }

  const preferredSite = SITE_FALLBACK_PRIORITY.reduce<SiteClaim | null>(
    (foundSite, siteNamePart) =>
      foundSite ??
      siteClaims.find(site =>
        normalizeSiteName(site.name).includes(siteNamePart),
      ) ??
      null,
    null,
  );

  if (preferredSite) {
    return preferredSite;
  }

  return siteClaims[0];
};

const isAllowedSite = (activeSite: SiteClaim, siteClaims: SiteClaim[]) =>
  siteClaims.length === 0 || siteClaims.some(site => site.id === activeSite.id);

const initialState = {
  identityClaims: null,
  authClaims: null,
  authStatus: LOADING_STATUS.LOADING,
  activeSite: null,
  activeSiteStatus: LOADING_STATUS.IDLE,
};

export const getUserClaimsAsync = createAsyncThunk(
  'auth/getUserClaims',
  async () => {
    const identityClaims = await userService.getIdentityClaims();
    const authClaims = await userService.getAuthorizationClaims();
    // console.log('identityClaims', identityClaims);
    // console.log('authClaims', authClaims);

    return { identityClaims, authClaims };
  },
);

export const getActiveSiteAsync = createAsyncThunk(
  'auth/getActiveSite',
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { authClaims } = state.auth;
    const siteClaims = getSiteClaimsFromAuthClaims(authClaims);

    const storedActiveSite = getStoredActiveSite();

    if (storedActiveSite && isAllowedSite(storedActiveSite, siteClaims)) {
      persistActiveSite(storedActiveSite);
      return storedActiveSite;
    }

    const defaultSite = getDefaultActiveSite(siteClaims);

    if (defaultSite) {
      persistActiveSite(defaultSite);
      return defaultSite;
    }

    return null;
  },
);

export const updateActiveSiteAsync = createAsyncThunk(
  'auth/updateActiveSite',
  async ({ id, name }: { id: string; name: string }) => {
    persistActiveSite({ id, name });
    return { id, name };
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getUserClaimsAsync.pending, state => {
        state.authStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getUserClaimsAsync.fulfilled, (state, action) => {
        state.identityClaims = action.payload.identityClaims;
        state.authClaims = action.payload.authClaims;
        state.authStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getUserClaimsAsync.rejected, state => {
        state.identityClaims = null;
        state.authClaims = null;
        state.authStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getActiveSiteAsync.pending, state => {
        state.activeSiteStatus = LOADING_STATUS.LOADING;
        state.activeSite = null;
      })
      .addCase(getActiveSiteAsync.fulfilled, (state, action) => {
        state.activeSite = action.payload;
        state.activeSiteStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getActiveSiteAsync.rejected, state => {
        state.activeSite = null;
        state.activeSiteStatus = LOADING_STATUS.FAILED;
      })
      .addCase(updateActiveSiteAsync.fulfilled, (state, action) => {
        state.activeSite = action.payload;
      });
  },
});

export const selectIdentityClaims = (state: RootState) =>
  state.auth.identityClaims;
export const selectAuthClaims = (state: RootState) => state.auth.authClaims;

export const selectRoleClaims = createSelector(
  [selectAuthClaims],
  authClaims => {
    const roleClaims = authClaims?.filter(
      (c: { type: CLAIM_TYPES }) => c.type === CLAIM_TYPES.RolesType,
    );
    return roleClaims?.map((roleClaim: { value: string }) => roleClaim.value);
  },
);

export const selectSiteClaims = createSelector(
  [selectAuthClaims],
  authClaims => {
    const siteClaims = authClaims?.filter(
      (c: { type: CLAIM_TYPES }) => c.type === CLAIM_TYPES.SitesType,
    );
    return siteClaims
      ?.map((siteClaim: { value: string }) =>
        parseSiteClaimValue(siteClaim.value),
      )
      .filter(Boolean);
  },
);

export const selectActiveSite = (state: RootState) => state.auth.activeSite;
export const selectActiveSiteStatus = (state: RootState) =>
  state.auth.activeSiteStatus;

export const selectLoginName = createSelector(
  [selectAuthClaims],
  authClaims => {
    const firstNameDictClaim = authClaims?.find(
      claim => claim.type === 'given_name',
    );
    const lastNameDictClaim = authClaims?.find(
      claim => claim.type === 'family_name',
    );
    return {
      firstName: firstNameDictClaim?.value ?? '',
      lastName: lastNameDictClaim?.value ?? '',
    };
  },
);

export const selectCurrentAccountId = createSelector(
  [selectAuthClaims],
  authClaims =>
    authClaims?.find((c: { type: string; value: string }) => c.type === 'id')
      ?.value ?? null,
);

export const selectLogoutUrl = createSelector(
  [selectIdentityClaims],
  identityClaims => {
    const logoutUrlDictClaim = identityClaims?.find(
      claim => claim.type === 'bff:logout_url',
    );
    return logoutUrlDictClaim?.value ?? '';
  },
);

export const selectAuthStatus = (state: RootState) => state.auth.authStatus;

export const checkRole = (validRoles?: Array<ROLES>) =>
  createSelector([selectAuthClaims], authClaims => {
    if (!validRoles || validRoles.length === 0) {
      return true;
    }

    if (!authClaims) {
      return false;
    }

    return authClaims.some(
      (item: { type: CLAIM_TYPES; value: ROLES }) =>
        item.type === CLAIM_TYPES.RolesType &&
        validRoles.includes(<ROLES>item.value),
    );
  });

export const checkPermission = (validPermissions?: Array<PERMISSIONS>) =>
  createSelector([selectAuthClaims], authClaims => {
    if (!validPermissions || validPermissions.length === 0) {
      return true;
    }

    if (!authClaims) {
      return false;
    }

    // If permission is TravelAgencyDomainUser and user has the role, return true early
    const hasTravelAgencyDomainPermission = validPermissions.includes(
      PERMISSIONS.TravelAgencyDomainUser,
    );
    const hasTravelAgencyDomainRole = authClaims.some(
      claim =>
        claim.type === CLAIM_TYPES.RolesType &&
        claim.value === ROLES.TravelAgencyDomainUser,
    );
    if (hasTravelAgencyDomainPermission && hasTravelAgencyDomainRole) {
      return true;
    }

    // Normal permission check
    return authClaims.some(
      (item: { type: CLAIM_TYPES; value: PERMISSIONS }) =>
        item.type === CLAIM_TYPES.PermissionsType &&
        validPermissions.includes(item.value),
    );
  });

export default authSlice.reducer;
